import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import ReactResizeDetector from 'react-resize-detector';
import { AgGridReact } from 'ag-grid-react';
import Fa from 'react-fontawesome';
import {
  reduce, filter,
  cloneDeep, indexOf, find,
  without, sortedUniq, first,
} from 'lodash';
import hashObject from 'object-hash';
import { 
  DropdownButton, 
  MenuItem, Modal, Button, ButtonGroup,
} from 'react-bootstrap';
import ProgressBar from '../../../../components/progress-bar';
import DataTypeCellRenderer from '../../components/cells/DataTypeCellRenderer';
import ToggleCellRenderer from '../../components/cells/ToggleFieldRenderer';
import APICellRenderer from '../../components/cells/APICellRenderer';
import FieldLabelCellRenderer from '../../components/cells/FieldLabelCellRenderer';
import CRMInstanceName from '../../../../components/crm-instance-name';


import SearchField from '../../../../components/search-field';
// Import Actions
import {
  requestFetchAllAmountFields,
  requestSyncAmountCustomFields,
  requestUpdateAmountFields,
} from '../../SetupActions';

// Import Selectors
import {
  getPossibleAmountFields,
  getAmountStages,
} from '../../SetupReducer';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getViewableDimensions } from '../../../App/AppReducer';
import styles from './styles.css';

class AmountPage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      error: null,
      allUpdating: false,
      fields: {},
      dirty: false,
      showingStage: null,
      hashedId: null,
      dirtyStages: {},
      showConfirmModal: false,
      mainNavBarHeight: 0,
      subNavBarHeight: 0,
      showStageSelector: false,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.loadCustomFields = this.loadCustomFields.bind(this);
    this.markAsAmountField = this.markAsAmountField.bind(this);
    this.submit = this.submit.bind(this);
    this.requestSyncCustomFields = this.requestSyncCustomFields.bind(this);
    this.showConfirm = this.showConfirm.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onResizeMainNavBar = this.onResizeMainNavBar.bind(this);
    this.onResizeSubNavBar = this.onResizeSubNavBar.bind(this);
    this.closeStageSelectorModal = this.closeStageSelectorModal.bind(this);
    this.columnValueGetter = this.columnValueGetter.bind(this);
    this.selectStage = this.selectStage.bind(this);
  }

  componentDidMount() {
    this.loadCustomFields();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  onResizeMainNavBar(width, mainNavBarHeight) {
    this.setState({ mainNavBarHeight });
  }
  onResizeSubNavBar(width, subNavBarHeight) {
    this.setState({ subNavBarHeight });
  }
  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }
  selectStage(showingStage) {
    this.setState({ showingStage, showStageSelector: false }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  loadCustomFields() {
    this.setState({
      loading: true, error: null, loaded: false, fields: [],
    });

    const allFieldsPromise = this.props.dispatch(requestFetchAllAmountFields());
    allFieldsPromise.then(
      (results) => {
        // clone!!!!! results.fields
        const fields = cloneDeep(results.fields || {});
        const hashedId = hashObject(fields);
        // is there any stages that has more than 1 values
        // or stages with one value different than 'Amount'
        const dirtyStages = reduce(fields, (items, s, k) => {
          items[k] = {
            hashed: hashObject(sortedUniq(s)),
            dirty: false,
          };
          return items;
        }, {});
        const showingStage = results.stages && results.stages.length ? results.stages[0].value : null;
        this.setState({
          loading: false, error: null, loaded: true, fields, showingStage, hashedId, dirtyStages,
        });
      },
      (error) => {
        this.setState({
          loading: false, error, loaded: false, fields: [],
        });
      },
    );
    return allFieldsPromise;
  }

  markAsAmountField(fieldObject) {
    const field = fieldObject.name;
    const { fields, dirtyStages, showingStage } = this.state;
    if (!fields[showingStage]) {
      fields[showingStage] = [];
    }
    let concernedStage = [...fields[showingStage]];

    const idx = indexOf(concernedStage, field);
    if (idx >= 0) {
      // remove it
      concernedStage = without(concernedStage, field);
    } else {
      concernedStage.push(field);
    }
    concernedStage = sortedUniq(concernedStage);
    const newFields = { ...fields, [showingStage]: concernedStage };
    const currentDirtyStage = dirtyStages[showingStage];
    const newDirtyStages = { ...dirtyStages, [showingStage]: { ...currentDirtyStage, dirty: currentDirtyStage.hashed !== hashObject(concernedStage) } };
    const newHasedId = hashObject(newFields);
    const dirty = newHasedId !== this.state.hashedId;
    this.setState({ dirty, dirtyStages: newDirtyStages, fields: { ...fields, [showingStage]: concernedStage } }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  showConfirm() {
    this.setState({ showConfirmModal: true });
  }
  closeModal() {
    const { disabled, allUpdating } = this.state;
    if (!disabled && !allUpdating) {
      this.setState({ showConfirmModal: false });
    }
  }
  submit() {
    const { fields, allUpdating } = this.state;
    if (allUpdating) {
      return;
    }
    this.setState({ allUpdating: true });
    this.props.dispatch(requestUpdateAmountFields(fields))
      .then(
        (results) => {
          const newFields = cloneDeep(results.fields);
          const hashedId = hashObject(newFields);
          const dirtyStages = reduce(newFields, (items, s, k) => {
            items[k] = {
              hashed: hashObject(sortedUniq(s)),
              dirty: false,
            };
            return items;
          }, {});
          this.setState({ 
            allUpdating: false,
            fields: newFields,
            hashedId,
            dirty: false,
            dirtyStages,
            showConfirmModal: false,
          });
        },
        () => {
          this.setState({ allUpdating: false });
        },
      );
  }
  closeStageSelectorModal() {
    this.setState({ showStageSelector: false });
  }

  requestSyncCustomFields() {
    this.props.dispatch(requestSyncAmountCustomFields());
  }

  
  columnValueGetter(params) {
    const { showingStage, fields } = this.state;
    const { data } = params;
    const fieldValue = data.name;
    const checked = indexOf(fields[showingStage], fieldValue) >= 0;
    return checked;
  }
  renderStageDropdown() {
    const { stages } = this.props;
    const {
      dirty, showingStage, dirtyStages,
    } = this.state;
    const activeStage = find(stages, { value: showingStage }) || first(stages);
    const title = activeStage !== undefined ? (
      <span>{dirty && <Fa name="circle" className="animated flash infinite text-warning" />} {activeStage.label}</span>
    ) : 'Select a Stage';

    return (
      <div>
        Select amount field(s) to use for
        &nbsp;
        <DropdownButton
          bsStyle="default"
          title={title}
          id="setup-amount-stage"
          onSelect={this.selectStage}
        >
          {stages.map((stage) => {
            const isDirty = dirtyStages[stage.value].dirty;
            return (
              <MenuItem
                key={stage.value}
                active={stage.value === showingStage}
                eventKey={stage.value}
              >
                <Fa name="circle" className={isDirty ? 'animated flash infinite text-warning' : 'invisible'} /> {stage.label}
              </MenuItem>
            );
          })}
        </DropdownButton>
        &nbsp;
        opportunity stage.
      </div>
    );
  }


  renderStageSelector() {
    const { stages } = this.props;
    const {
      dirty, showingStage, dirtyStages, showStageSelector, disabled,
    } = this.state;
    const activeStage = find(stages, { value: showingStage }) || first(stages);
    const title = activeStage !== undefined ? (
      <span>{dirty && <Fa name="circle" className="animated flash infinite text-warning" />} {activeStage.label}</span>
    ) : 'Select a Stage';

    return (
      <div>
        <div>
          Select amount field(s) to use for
          &nbsp;
          <Button 
            bsStyle="default"
            disabled={disabled}
            onClick={() => { this.setState({ showStageSelector: true }); }}
          >
            {title} <Fa name="caret-down" />
          </Button>
          &nbsp;
          opportunity stage.
        </div>
        <Modal show={showStageSelector} bsSize="sm" onHide={this.closeStageSelectorModal} keyboard={!disabled}>
          <Modal.Header closeButton>
            <Modal.Title>Select a Stage</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <ButtonGroup block vertical>
                {stages.map((stage) => {
                  const isDirty = dirtyStages[stage.value].dirty;
                  return (
                    <Button
                      key={stage.value}
                      active={stage.value === showingStage}
                      onClick={() => {
                        this.selectStage(stage.value);
                      }}
                    >
                      <Fa name="circle" className={isDirty ? 'animated flash infinite text-warning' : 'invisible'} /> {stage.label}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    );

  }

  renderTable() {
    const { viewableDimensions, possibleFields } = this.props;
    const {
      fields, allUpdating, dirty,
      showingStage, dirtyStages,
      mainNavBarHeight, subNavBarHeight,
    } = this.state;
    const numberOfDirtyStages = filter(dirtyStages, { dirty: true }).length;
    if (possibleFields.length === 0) {
      return (
        <div className="alert alert-danger">
          No custom fields of type <code>currency</code>, <code>number</code> nor <code>formula</code> found.
        </div>
      );
    }
    // sync_required
    const filteredFields = reduce(possibleFields, (items, possibleField) => {
      if (possibleField.sync_required === true || indexOf(fields[showingStage], possibleField.name) >= 0) {
        items.push({
          ...possibleField,
          is_default: possibleField.name === 'Amount',
        });
      };
      return items;
    }, []);

    const viewableHeight = viewableDimensions.height - mainNavBarHeight - subNavBarHeight;
    const columnDefs = [
      {
        headerName: 'Field Name',
        field: 'label',
        suppressMenu: true,
        width: 450,
        suppressMovable: true,
        cellRenderer: FieldLabelCellRenderer,
        pinned: 'left',
      },
      {
        headerName: 'API Name',
        field: 'name',
        suppressMenu: true,
        width: 365,
        cellRenderer: APICellRenderer,
        suppressMovable: true,
      },
      {
        headerName: 'Data Type',
        field: 'type',
        suppressMenu: true,
        width: 120,
        suppressMovable: true,
        cellRenderer: DataTypeCellRenderer,
      },
      {
        headerName: 'Use to Calculate Amount',
        suppressMenu: true,
        width: 200,
        valueGetter: this.columnValueGetter,
        suppressMovable: true,
        cellRenderer: ToggleCellRenderer,
        cellRendererParams: {
          onToggle: this.markAsAmountField,
          disabled: allUpdating,
        },
      },  
    ];


    return (
      <div>
        {possibleFields.length > 0 && 
          <nav className="navbar navbar-default navbar-controls">
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
            {dirty && 
              <div className="navbar-form navbar-right animated flash" style={{ marginRight: 5 }}>
                <Button onClick={this.showConfirm} disabled={allUpdating} bsStyle="primary">
                  Save &amp; Update {numberOfDirtyStages} {pluralize('stage', numberOfDirtyStages)}
                </Button>
              </div>
            }
            <ReactResizeDetector handleHeight onResize={this.onResizeMainNavBar} />
          </nav>
        }
        {possibleFields.length > 0 && 
          <nav className={`navbar navbar-default navbar-controls ${styles.navBar}`}>
            <div className="navbar-form navbar-left">
              {this.renderStageSelector()}
              <ReactResizeDetector handleHeight onResize={this.onResizeSubNavBar} />
            </div>
          </nav>
        }
        <div className="ag-theme-alpine" style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            columnDefs={columnDefs}
            getRowNodeId={data => data._id}
            onGridReady={this.onGridReady}
            rowData={filteredFields}
          />
        </div>
      </div>
    );
  }

  renderInstruction(orgType) {
    let opportunityObjectName = 'Opportunity';
    if (orgType === 'hubspot') {
      opportunityObjectName = 'deal';
    }

    return (
      <div className="well well-sm">
        <div>
          <p>
            The table below contains the fields on your organization&rsquo;s <code>Opportunity</code> object
            that can be used to calculate the total amount, or size, for an opportunity in TruVoice Automation.
            This amount field(s) can be set individually for each opportunity stage.
            Select the field, or fields, that best represent the total amount for sales opportunities
            based on how your organization is currently using <CRMInstanceName />.
          </p>
          <p>
            NOTE: 
          </p>
          <ul>
            <li>
              If you select more than one field,
              TruVoice Automation will sum the fields together to determine the amount,
              or size, for each opportunity.
            </li>
            <li>
              If you cannot find the field(s) you are looking for, you can also <Link to={`/setup/sync-settings/${opportunityObjectName}`}>add more custom fields to the sync</Link>.
            </li>
          </ul>
        </div>
      </div>
    );
  }

  renderSyncButton() {
    const syncing = false;
    return (
      <div>
        <span>
          {!syncing && 
            <button
              type="button"
              className="btn btn-sm btn-default"
              onClick={this.requestSyncCustomFields}
            >
              <Fa name="refresh" /> Sync custom fields with <CRMInstanceName />
            </button>
          }
          {syncing && <button type="button" disabled={syncing} className="btn btn-sm btn-default"><Fa name="refresh" spin /> Syncing</button>}
        </span>
      </div>
    );
  }

  renderConfirmModal() {
    const { showConfirmModal, disabled, allUpdating } = this.state;
    return (
      <Modal show={showConfirmModal} bsSize="sm" onHide={this.closeModal} keyboard={!disabled}>
        <Modal.Body>
          <div className="alert alert-warning">
            <Fa name="exclamation-triangle" /> Update may trigger opportunity amount history logs on TruVoice Automation.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="clearfix">
            <Button className="pull-right" bsStyle="primary" disabled={disabled || allUpdating} onClick={this.submit}>
              {allUpdating && <span>Updating</span>}
              {!allUpdating && <span>Update</span>}
            </Button>
            <Button className="pull-left" bsStyle="link" disabled={disabled || allUpdating} onClick={this.closeModal}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    const { loading, loaded } = this.state;
    const { organization } = this.props;
    const isDemo = organization && organization.is_demo;
    const orgType = organization && organization.type;
    return (
      <div>
        <Helmet title="Opportunity Amount Integrations" />
        <div className="container">
          {!isDemo && this.renderInstruction(orgType)}
          {loading && <ProgressBar />}
          {!loading && loaded && <div>
            {!isDemo && this.renderTable()}
            {isDemo && <div className="alert alert-warning">
              This is a demo organization, please switch back to the original organization to change the amound field mapping.
            </div>}
          </div>}
        </div>
        {this.renderConfirmModal()}
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    viewableDimensions: getViewableDimensions(state),
    possibleFields: getPossibleAmountFields(state),
    stages: getAmountStages(state),
  };
}

AmountPage.propTypes = {
  organization: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({}).isRequired,
  viewableDimensions: PropTypes.shape({}).isRequired,
  possibleFields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  stages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

AmountPage.contextTypes = {
  router: PropTypes.object,
};

export default connect(mapStateToProps)(AmountPage);
